import {
  PLATFORMS,
  type CampaignStatus,
  type CreatorStatus,
  type Platform,
  type SocialVerificationStatus,
} from './enums.js';

/**
 * The campaign rules both sides compute the same way, and the constants a
 * screen needs at runtime.
 *
 * NO ZOD HERE, ON PURPOSE. The request and response schemas live in
 * `./contracts.ts`; eagerly built zod objects do not tree-shake, so a barrel
 * that re-exported them would put every server-side validation rule in the
 * browser bundle. This file is plain types, plain constants and one pure
 * function — all of which the Campaigns screen genuinely uses.
 */

/* ------------------------------------------------------------------ */
/* Source links                                                        */
/* ------------------------------------------------------------------ */

/**
 * What a creator may cut from, or should look at first.
 *
 * The first four are the original set and stay exactly where they are, because
 * `CampaignTermsVersion.briefJson` carries the kind in the frozen document text
 * and a renamed kind would re-hash every acceptance. The four after them are
 * the "Reference materials" block on the campaign page: the client's own
 * social channels (`YOUTUBE`, `INSTAGRAM`, `TIKTOK`), which a creator studies
 * for tone before cutting anything, and a `DOC` — a brief, a brand sheet, a
 * Google Doc of dos and don'ts.
 */
export const SOURCE_LINK_KINDS = [
  'CHANNEL',
  'VOD',
  'FILE',
  'OTHER',
  'YOUTUBE',
  'INSTAGRAM',
  'TIKTOK',
  'DOC',
] as const;
export type SourceLinkKind = (typeof SOURCE_LINK_KINDS)[number];

/** What each kind is called on a page. */
export const SOURCE_LINK_KIND_LABELS: Readonly<Record<SourceLinkKind, string>> = Object.freeze({
  CHANNEL: 'Channel',
  VOD: 'Stream recording',
  FILE: 'File',
  OTHER: 'Link',
  YOUTUBE: 'YouTube',
  INSTAGRAM: 'Instagram',
  TIKTOK: 'TikTok',
  DOC: 'Document',
});

/** The kinds that are the client's own social presence, in the order they are listed. */
export const SOCIAL_SOURCE_LINK_KINDS: readonly SourceLinkKind[] = ['YOUTUBE', 'INSTAGRAM', 'TIKTOK'];

/**
 * The kind a pasted URL most likely is, for an operator who did not say.
 *
 * Host-based and deliberately conservative: a `youtube.com/@handle` is the
 * client's channel, a `youtube.com/watch` is a video to cut from, and anything
 * this cannot place stays `OTHER` rather than guessing. The operator can always
 * override it; this only saves them the select on the obvious ones.
 */
export function guessSourceLinkKind(url: string): SourceLinkKind {
  /* A regex and not `new URL`: this file is shared with the browser bundle
     and typed against no DOM lib, and a host and a path are all the guess
     needs. */
  const match = /^https?:\/\/([^/?#]+)([^?#]*)/i.exec(url.trim());
  if (!match) return 'OTHER';
  const host = match[1]!.toLowerCase().replace(/^www\./, '');
  const pathname = match[2] ?? '';
  if (host === 'youtube.com' || host === 'youtu.be' || host === 'm.youtube.com') {
    return /^\/(@|channel\/|c\/|user\/)/.test(pathname) ? 'YOUTUBE' : 'VOD';
  }
  if (host === 'instagram.com') return /^\/(p|reel|reels)\//.test(pathname) ? 'VOD' : 'INSTAGRAM';
  if (host === 'tiktok.com' || host === 'vm.tiktok.com') {
    return /\/video\//.test(pathname) ? 'VOD' : 'TIKTOK';
  }
  if (host === 'twitch.tv') return /^\/videos\//.test(pathname) ? 'VOD' : 'CHANNEL';
  if (host === 'docs.google.com' || host === 'notion.so' || host.endsWith('.notion.site')) {
    return 'DOC';
  }
  if (host === 'drive.google.com' || host === 'dropbox.com' || host === 'wetransfer.com') {
    return 'FILE';
  }
  return 'OTHER';
}

/* ------------------------------------------------------------------ */
/* Rates per platform                                                  */
/* ------------------------------------------------------------------ */

/**
 * The creator rate per platform, in cents per 1,000 verified views.
 *
 * A platform that is absent pays the campaign's `ratePerThousandCents`. That
 * fallback is the whole design: every campaign created before rates went per
 * platform stores `{}` and keeps paying the one figure it promised, and an
 * operator who wants one rate for everything types one number.
 */
export type PlatformRates = Partial<Record<Platform, number>>;

/** The rate a post on `platform` is paid at. Never below zero, never a float. */
export function rateForPlatform(
  campaign: { ratePerThousandCents: number; platformRates: PlatformRates },
  platform: Platform,
): number {
  const override = campaign.platformRates[platform];
  const rate =
    typeof override === 'number' && Number.isFinite(override) ? override : campaign.ratePerThousandCents;
  return Math.max(0, Math.floor(rate));
}

/** One entry per allowed platform, in `PLATFORMS` order, with the rate it pays. */
export function ratesByPlatform(campaign: {
  ratePerThousandCents: number;
  platformRates: PlatformRates;
  allowedPlatforms: Platform[];
}): Array<{ platform: Platform; ratePerThousandCents: number }> {
  return PLATFORMS.filter((p) => campaign.allowedPlatforms.includes(p)).map((platform) => ({
    platform,
    ratePerThousandCents: rateForPlatform(campaign, platform),
  }));
}

/**
 * Does the campaign pay one rate everywhere? True for a campaign with no
 * platforms yet too — there is nothing to differ.
 */
export function ratesAreUniform(campaign: {
  ratePerThousandCents: number;
  platformRates: PlatformRates;
  allowedPlatforms: Platform[];
}): boolean {
  const rates = ratesByPlatform(campaign).map((r) => r.ratePerThousandCents);
  return new Set(rates).size <= 1;
}

/* ------------------------------------------------------------------ */
/* Per-submission cap                                                  */
/* ------------------------------------------------------------------ */

/**
 * The most one clip can earn, applied to the gross figure.
 *
 * Pure and shared so the calculator, the estimator on the campaign page and
 * the tests are the same arithmetic. `null` is no cap.
 */
export function applyPerSubmissionCap(grossCents: number, capCents: number | null): number {
  if (capCents === null || !Number.isFinite(capCents)) return grossCents;
  return Math.min(grossCents, Math.max(0, Math.floor(capCents)));
}

/* ------------------------------------------------------------------ */
/* Joining: open, or apply first                                       */
/* ------------------------------------------------------------------ */

/**
 * How a creator gets onto a campaign.
 *
 * `OPEN`: pressing Join records the membership at once, which is the pilot's
 * default and the only mode that existed before migration 0011. `APPLY`: Join
 * becomes Apply, records an application, and the operator approves or declines
 * it from the campaign's admin page; nothing can be submitted until then.
 *
 * Pinned by triggers (0011), like `category`, because a CHECK on `Campaign`
 * would rebuild the table.
 */
export const CAMPAIGN_JOIN_MODES = ['OPEN', 'APPLY'] as const;
export type CampaignJoinMode = (typeof CAMPAIGN_JOIN_MODES)[number];

/**
 * The state of an application, on `CampaignParticipation.applicationStatus`.
 *
 * NULL on the row is "no application" — an OPEN-mode join. The membership
 * column `status` is CHECK-pinned to JOINED | WITHDRAWN | REMOVED by migration
 * 0000 and cannot grow without a rebuild, so an application lives beside it
 * rather than in it; see `participationViewStatus` for how the two are read
 * together.
 */
export const CAMPAIGN_APPLICATION_STATUSES = ['PENDING', 'APPROVED', 'DECLINED'] as const;
export type CampaignApplicationStatus = (typeof CAMPAIGN_APPLICATION_STATUSES)[number];

/**
 * What a creator is SHOWN about their participation: the three membership
 * states plus the two application states that are not memberships yet.
 */
export const PARTICIPATION_VIEW_STATUSES = [
  'JOINED',
  'WITHDRAWN',
  'REMOVED',
  'APPLIED',
  'DECLINED',
] as const;
export type ParticipationViewStatus = (typeof PARTICIPATION_VIEW_STATUSES)[number];

/**
 * The one place the (status, applicationStatus) pair becomes a word.
 *
 * A PENDING application is `APPLIED` and a DECLINED one is `DECLINED`,
 * whatever the membership column says (the 0011 trigger guarantees it is not
 * JOINED). Everything else is the membership status as stored — an APPROVED
 * application is simply a JOINED row with a history.
 */
export function participationViewStatus(row: {
  status: 'JOINED' | 'WITHDRAWN' | 'REMOVED';
  applicationStatus: CampaignApplicationStatus | null;
}): ParticipationViewStatus {
  if (row.applicationStatus === 'PENDING') return 'APPLIED';
  if (row.applicationStatus === 'DECLINED') return 'DECLINED';
  return row.status;
}

/** What a creator whose application is waiting is told, everywhere it comes up. */
export const APPLICATION_PENDING_MESSAGE =
  'Your application to this campaign is waiting on the operator. You can submit a clip once it is approved.';

/** The same, for a declined application: the operator's own sentence, always. */
export function applicationDeclinedMessage(reason: string | null): string {
  const text = (reason ?? '').trim();
  return text
    ? `Your application to this campaign was declined: ${text}`
    : 'Your application to this campaign was declined.';
}

/* ------------------------------------------------------------------ */
/* Finding a campaign                                                  */
/* ------------------------------------------------------------------ */

/**
 * The longest search `GET /campaigns?q=` accepts, and the `maxLength` the box
 * on the Campaigns page carries.
 *
 * ONE NUMBER, in a file the browser already ships, so the input cannot ask for
 * something the query schema refuses. A pasted paragraph in the search box was
 * a 400 that the page answered by leaving the previous results on screen.
 */
export const CAMPAIGN_SEARCH_MAX_LENGTH = 200;

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

/**
 * The niche a campaign is in, which is how a creator finds one.
 *
 * ONE FLAT LIST, AND `other` IS A REAL ANSWER. A creator opening Nexus wants
 * "show me the music ones", not a taxonomy; and an operator setting up a
 * campaign at 1am must never be blocked because the client's niche is not on
 * a list somebody else wrote. So the list is short, the default is `other`,
 * and nothing is ever refused for want of a better word.
 *
 * LOWER CASE ON PURPOSE. The value is what goes in the URL (`?category=music`)
 * and in the column, so it is the spelling a creator can type and a support
 * conversation can quote. The display label is the only thing with capitals in
 * it, and it lives in `CAMPAIGN_CATEGORY_LABELS` so the pill row, the admin
 * select and the chip on a card cannot drift into three different words for
 * one niche.
 *
 * ADDING ONE IS A MIGRATION. The column is pinned to this list by a pair of
 * triggers (migration 0010) rather than by a CHECK, because on D1 a CHECK means
 * rebuilding `Campaign` — the table `CampaignParticipation`, `PostSubmission`
 * and `Earning` all point at. A new category is therefore: this array, and a
 * migration that drops and recreates the two triggers with the longer list.
 */
export const CAMPAIGN_CATEGORIES = [
  'music',
  'podcast',
  'gaming',
  'streaming',
  'sports',
  'comedy',
  'lifestyle',
  'business',
  'education',
  'beauty',
  'fashion',
  'food',
  'tech',
  'logo',
  'other',
] as const;
export type CampaignCategory = (typeof CAMPAIGN_CATEGORIES)[number];

/** What a creator reads. `logo` is the one whose value does not say itself. */
export const CAMPAIGN_CATEGORY_LABELS: Readonly<Record<CampaignCategory, string>> = Object.freeze({
  music: 'Music',
  podcast: 'Podcast',
  gaming: 'Gaming',
  streaming: 'Streaming',
  sports: 'Sports',
  comedy: 'Comedy',
  lifestyle: 'Lifestyle',
  business: 'Business',
  education: 'Education',
  beauty: 'Beauty',
  fashion: 'Fashion',
  food: 'Food',
  tech: 'Tech',
  logo: 'Brand / logo placement',
  other: 'Other',
});

/** The default every campaign starts in, and what an unreadable value reads as. */
export const DEFAULT_CAMPAIGN_CATEGORY: CampaignCategory = 'other';

/**
 * The label for a category value, defending against a value this build has
 * never heard of.
 *
 * The column is pinned by triggers, so an unknown value cannot be written by
 * this codebase — but a browser tab left open across a deploy that ADDS a
 * category will be handed one, and a chip that renders `undefined` is worse
 * than a chip that renders the value.
 */
export function campaignCategoryLabel(value: string | null | undefined): string {
  if (!value) return CAMPAIGN_CATEGORY_LABELS[DEFAULT_CAMPAIGN_CATEGORY];
  return (
    CAMPAIGN_CATEGORY_LABELS[value as CampaignCategory] ??
    value.replace(/[_-]+/g, ' ').replace(/^./, (ch) => ch.toUpperCase())
  );
}

/** Is this a category this build knows? Used where a person typed the value. */
export function isCampaignCategory(value: string): value is CampaignCategory {
  return (CAMPAIGN_CATEGORIES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Status transitions                                                  */
/* ------------------------------------------------------------------ */

/**
 * Which status may follow which.
 *
 * `COMPLETED` and `CANCELLED` are terminal on purpose: a campaign that has
 * stopped paying must not be able to start paying again by way of a status
 * flip, because creators' caps, the funded budget and the frozen terms all
 * hang off "this campaign was live between these instants".
 */
export const CAMPAIGN_TRANSITIONS: Readonly<Record<CampaignStatus, readonly CampaignStatus[]>> =
  Object.freeze({
    DRAFT: ['FUNDED', 'CANCELLED'],
    FUNDED: ['ACTIVE', 'DRAFT', 'CANCELLED'],
    ACTIVE: ['PAUSED', 'COMPLETED', 'CANCELLED'],
    PAUSED: ['ACTIVE', 'COMPLETED', 'CANCELLED'],
    COMPLETED: [],
    CANCELLED: [],
  });

/**
 * The fields a creator was promised when they accepted the campaign's terms.
 * Once the campaign has been live, these are frozen: changing the rate under
 * someone who already posted is not an edit, it is a renegotiation.
 */
export const ECONOMIC_CAMPAIGN_FIELDS = [
  'ratePerThousandCents',
  /* Phase 5. Changing what the client pays after creators have joined changes
     what WeClip keeps on work already done, which is the same kind of
     retrospective edit the creator rate is frozen against. */
  'clientRatePerThousandCents',
  'perCreatorCapCents',
  /* Campaign parity. The most one clip can earn is as much a term as the rate
     is: a creator who read "up to $50 per clip" and posted accepted that
     figure. `platformRates` is frozen the same way but is not on this list,
     because the list is compared field-for-field against the campaign row and
     the rates live in a JSON column (`platformRatesJson`); the admin route
     compares them itself. */
  'perSubmissionCapCents',
  'budgetCents',
  'measurementWindowHours',
  'holdbackDays',
  'minPayableViews',
  'minAccountAgeDays',
  'minFollowers',
  /* Phase 4. How long the post must stay up is part of the deal a creator
     accepted -- shortening it after the fact would pay for posts that came
     down, and lengthening it would bind them to something they never read.
     `fraudThresholds` is deliberately NOT here: the plan says those get
     calibrated on real traffic during the pilot, and a threshold nobody can
     move on a live campaign is a threshold that holds every submission until
     the campaign ends. */
  'requiredLiveDays',
] as const;

/** Statuses in which the economic fields may no longer be edited. */
export const CAMPAIGN_ECONOMICS_FROZEN_STATUSES: readonly CampaignStatus[] = [
  'ACTIVE',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
];

/* ------------------------------------------------------------------ */
/* What a creator is shown                                             */
/* ------------------------------------------------------------------ */

/**
 * Why a creator cannot take a campaign. Codes so a test can assert the reason
 * rather than a sentence; the message is what the screen renders.
 */
export const ELIGIBILITY_REASON_CODES = [
  'no_connected_account',
  'no_account_on_allowed_platform',
  'account_too_new',
  'account_age_unknown',
  'not_enough_followers',
  'creator_not_approved',
  'campaign_not_started',
  'campaign_ended',
  'submission_deadline_passed',
  'campaign_not_active',
  'per_creator_cap_reached',
  'removed_from_campaign',
] as const;
export type EligibilityReasonCode = (typeof ELIGIBILITY_REASON_CODES)[number];

export interface EligibilityReason {
  code: EligibilityReasonCode;
  /** Shown to the creator verbatim. */
  message: string;
}

export interface Eligibility {
  eligible: boolean;
  reasons: EligibilityReason[];
  /**
   * WHICH accounts clear this campaign's floors, by handle.
   *
   * The floors pass if ANY of a creator's accounts on an allowed platform
   * meets them, which with five accounts per platform means "you qualify" can
   * be true because of exactly one of them. A creator who reads "You qualify"
   * and posts from a different account has done nothing wrong and will still
   * be refused at submission (`ACCOUNT_ELIGIBILITY`), so the card has to name
   * the account the verdict was about.
   *
   * Empty whenever the verdict is not about accounts at all — a campaign that
   * has not started, a creator who is not approved — because a list of
   * qualifying handles beside "this campaign has ended" says nothing.
   */
  qualifyingHandles: string[];
}

/** The creator-side facts the rule needs. */
export interface EligibilityAccount {
  platform: Platform;
  /** `@handle`, as the Connections screen shows it. Named on the card. */
  handle: string;
  verificationStatus: SocialVerificationStatus;
  followerCountAtConnect: number | null;
  /** ISO-8601, or null where the platform does not expose it. */
  accountCreatedAt: string | null;
}

/**
 * What a creator is told when the post is on an account that does not clear
 * the campaign's follower floor.
 *
 * THE FLOOR IS ABOUT THE ACCOUNT YOU POST FROM. A creator with a 1,200-
 * follower TikTok and a 500-follower one qualifies for a 1,000-follower
 * campaign — on the big account. The clip they posted from the small one is
 * not what the client paid for, and it has to be refused with the numbers in
 * it: "you do not qualify" is untrue (they do, on the other account) and
 * unactionable.
 *
 * Shared so the check, the card and the tests say the same thing.
 */
export function belowFollowerFloorMessage(input: {
  handle: string;
  followers: number;
  minFollowers: number;
  readAt: string | null;
}): string {
  const read = input.readAt ? ` (read on ${input.readAt.slice(0, 10)})` : '';
  return `This campaign needs at least ${input.minFollowers.toLocaleString('en-US')} followers on the account you post from, and ${input.handle} has ${input.followers.toLocaleString('en-US')}${read}. Post from an account that clears the floor, or press Re-verify on Connections if that number is out of date and submit again.`;
}

/** The same fact, when nobody can read the count at all. */
export function unknownFollowerCountMessage(handle: string, minFollowers: number): string {
  return `This campaign needs at least ${minFollowers.toLocaleString('en-US')} followers on the account you post from, and we have no follower count for ${handle} — the platform hides it. An operator will look at this one.`;
}

export interface EligibilityCampaign {
  status: CampaignStatus;
  startsAt: string | null;
  endsAt: string | null;
  submissionDeadline: string | null;
  allowedPlatforms: Platform[];
  minAccountAgeDays: number;
  minFollowers: number;
  perCreatorCapCents: number | null;
}

export interface EligibilityCreator {
  status: CreatorStatus;
  /**
   * The operator's sentence for a REJECTED or SUSPENDED profile.
   *
   * Written when the decision was recorded and shown to the creator verbatim:
   * "your account is not approved" with no cause is the message that produces
   * a support conversation, and the sentence already exists on the row.
   */
  statusReason: string | null;
  /** Cents already earned on this campaign. */
  earnedCents: number;
  /** Per-creator cap for this creator specifically, when the operator set one. */
  capOverrideCents: number | null;
  /** `REMOVED` participation means the operator took them off this campaign. */
  removedFromCampaign: boolean;
}

/**
 * What a creator who is not APPROVED is told, in one place.
 *
 * Three different facts wore one sentence — "still being reviewed" — so a
 * rejected creator waited forever for a decision that had already been made,
 * and a suspended one had no idea anything had happened. The reason the
 * operator wrote is appended because it is the only part that says what to do
 * about it.
 */
export function notApprovedMessage(
  status: CreatorStatus,
  statusReason: string | null,
): string {
  const reason = (statusReason ?? '').trim();
  if (status === 'SUSPENDED') {
    return reason
      ? `Your creator account is suspended: ${reason}`
      : 'Your creator account is suspended.';
  }
  if (status === 'REJECTED') {
    return reason
      ? `Your creator account was not approved: ${reason}`
      : 'Your creator account was not approved.';
  }
  return 'Your creator account is still being reviewed.';
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Can this creator take this campaign, and if not, exactly why.
 *
 * Pure, and shared, so the Campaigns screen and the submission route in phase 3
 * cannot drift into two different answers. Every failing condition is collected
 * — the screen shows *all* the reasons, because "connect an account" followed a
 * week later by "and you need 1,000 followers" is how a pilot loses people.
 *
 * A creator with no connected account is simply ineligible with
 * `no_connected_account`; that is the expected state on day one and is not an
 * error anywhere.
 */
export function evaluateEligibility(
  campaign: EligibilityCampaign,
  creator: EligibilityCreator | null,
  accounts: EligibilityAccount[],
  now: string,
): Eligibility {
  const reasons: EligibilityReason[] = [];
  const nowMs = Date.parse(now);

  if (campaign.status !== 'ACTIVE') {
    reasons.push({
      code: 'campaign_not_active',
      message: 'This campaign is not accepting submissions right now.',
    });
  }
  if (campaign.startsAt && Date.parse(campaign.startsAt) > nowMs) {
    reasons.push({
      code: 'campaign_not_started',
      message: `This campaign opens on ${campaign.startsAt.slice(0, 10)}.`,
    });
  }
  if (campaign.endsAt && Date.parse(campaign.endsAt) <= nowMs) {
    reasons.push({ code: 'campaign_ended', message: 'This campaign has ended.' });
  }
  if (campaign.submissionDeadline && Date.parse(campaign.submissionDeadline) <= nowMs) {
    reasons.push({
      code: 'submission_deadline_passed',
      message: 'The deadline for submitting to this campaign has passed.',
    });
  }

  if (creator === null) {
    reasons.push({
      code: 'no_connected_account',
      message: 'Connect a TikTok, Instagram or YouTube account to take this campaign.',
    });
    return { eligible: false, reasons, qualifyingHandles: [] };
  }

  if (creator.removedFromCampaign) {
    reasons.push({
      code: 'removed_from_campaign',
      message: 'You have been removed from this campaign.',
    });
  }
  if (creator.status !== 'APPROVED') {
    reasons.push({
      code: 'creator_not_approved',
      message: notApprovedMessage(creator.status, creator.statusReason),
    });
  }

  const cap = creator.capOverrideCents ?? campaign.perCreatorCapCents;
  if (cap !== null && creator.earnedCents >= cap) {
    reasons.push({
      code: 'per_creator_cap_reached',
      message: 'You have reached the maximum you can earn on this campaign.',
    });
  }

  /* Only a proven account counts: a PENDING or CHALLENGE_ISSUED connection is
     a claim, not a connection, and paying on a claim is the whole fraud. */
  const verified = accounts.filter((a) => a.verificationStatus === 'VERIFIED');
  if (verified.length === 0) {
    reasons.push({
      code: 'no_connected_account',
      message: 'Connect a TikTok, Instagram or YouTube account to take this campaign.',
    });
    return { eligible: false, reasons, qualifyingHandles: [] };
  }

  const onPlatform =
    campaign.allowedPlatforms.length === 0
      ? []
      : verified.filter((a) => campaign.allowedPlatforms.includes(a.platform));
  if (onPlatform.length === 0) {
    reasons.push({
      code: 'no_account_on_allowed_platform',
      message: `This campaign runs on ${campaign.allowedPlatforms.join(', ') || 'no platform yet'}. Connect an account there.`,
    });
    return { eligible: reasons.length === 0, reasons, qualifyingHandles: [] };
  }

  /* The floors are met if ANY allowed-platform account meets them: a creator
     with a big TikTok and a new Instagram is eligible, and posts from the
     TikTok. Phase 3 checks the account the post is actually on. */
  /**
   * ACCOUNT AGE IS ONLY KNOWABLE ON YOUTUBE.
   *
   * `@weclip/stats-providers` returns `createdAt` from a channel's
   * `publishedAt`; TikTok's profile payload and Instagram's carry no creation
   * time at all, so `accountCreatedAt` is null for every account on those two
   * and always will be. A campaign with an age floor therefore excludes every
   * TikTok and Instagram creator — which is a legitimate thing for an operator
   * to mean and a catastrophic thing to say wrongly, because "your account
   * must be at least 30 days old" told to somebody whose account is four years
   * old reads as a bug in Nexus and loses them.
   *
   * So the two cases are separated: too new is too new, and unknown says it is
   * unknown and on which platform. Both still refuse — an age floor that
   * passed accounts whose age nobody could check would not be a floor.
   */
  const withKnownAge = onPlatform.filter((a) => a.accountCreatedAt !== null);
  const oldEnough = withKnownAge.filter((a) => {
    if (campaign.minAccountAgeDays <= 0) return true;
    return nowMs - Date.parse(a.accountCreatedAt!) >= campaign.minAccountAgeDays * DAY_MS;
  });
  if (campaign.minAccountAgeDays <= 0) {
    // No floor: the age nobody can read does not matter.
  } else if (withKnownAge.length === 0) {
    const platforms = [...new Set(onPlatform.map((a) => a.platform))].join(' or ');
    reasons.push({
      code: 'account_age_unknown',
      message: `This campaign only takes accounts at least ${campaign.minAccountAgeDays} days old, and ${platforms} does not publish when an account was created — so we cannot check it. Ask the operator.`,
    });
  } else if (oldEnough.length === 0) {
    reasons.push({
      code: 'account_too_new',
      message: `Your account must be at least ${campaign.minAccountAgeDays} days old for this campaign.`,
    });
  }

  const bigEnough = (campaign.minAccountAgeDays > 0 ? oldEnough : onPlatform).filter(
    (a) => campaign.minFollowers <= 0 || (a.followerCountAtConnect ?? 0) >= campaign.minFollowers,
  );
  if (bigEnough.length === 0 && campaign.minFollowers > 0) {
    reasons.push({
      code: 'not_enough_followers',
      message: `This campaign needs at least ${campaign.minFollowers.toLocaleString('en-US')} followers.`,
    });
  }

  return {
    eligible: reasons.length === 0,
    reasons,
    /* The accounts the verdict was actually about: on an allowed platform,
       old enough where an age floor applies, and over the follower floor. The
       submission check refuses a post from anything not on this list, so the
       card names it before a creator cuts the clip. */
    qualifyingHandles: bigEnough.map((a) => a.handle),
  };
}

